<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import Carousel from 'svelte-carousel';
  import Button from '$lib/components/control/Button.svelte';
  import TextInput from '$lib/components/control/TextInput.svelte';
  import ArticleStandardCard from '$lib/components/website/ArticleStandardCard.svelte';
  import CtaPanel from '$lib/components/website/ctaPanel/CtaPanel.svelte';
  import {
    BUTTON_VARIANT,
    BUTTON_SIZE,
    ARTICLE_LISTING_SKIP,
    ARTICLE_CATEGORY_CARDS,
  } from '$lib/constants/constants';
  import { capitaliseString } from '$lib/helpers/commonHelpers';
  import SearchIcon from '../../../../src/assets/svgs/search.svelte';
  import CloseIcon from '../../../../src/assets/svgs/close.svelte';
  import ThemedHeader from '$lib/components/website/ThemedHeader.svelte';
  import Breadcrumbs from '$lib/components/website/Breadcrumbs.svelte';

  export let data;

  let skip = ARTICLE_LISTING_SKIP;
  let moreArticles = [];
  let carousel: any;

  $: category = $page.params.category;

  $: heroArticle = data?.pageData?.heroArticle;
  $: featuredArticlesCollection = data?.pageData?.featuredArticlesCollection;
  $: footerCtaPanel = data?.pageData?.footerCtaPanel;
  $: totalArticle = data?.pageData?.articles?.totalArticle || 0;
  $: displayLoadMoreButton = data?.pageData?.articles?.articleList?.length
    ? data?.pageData?.articles?.articleList?.length + moreArticles?.length < totalArticle
    : false;
  $: searchParams = $page.url?.searchParams?.get('search');
  $: clearButtonDisplay = searchParams ? 'flex' : 'hidden';

  const resetMoreArticles = () => {
    skip = ARTICLE_LISTING_SKIP;
    moreArticles = [];
  };

  const fetchMoreArticles = async (_e) => {
    const currentUserSearch = $page.url?.searchParams?.get('search');
    if (currentUserSearch) {
      const res = await fetch(`/api/customApiArticles?skip=${skip}&search=${currentUserSearch}`);
      const moreArticlesResponse = await res.json();
      if (moreArticlesResponse?.articleList?.length) {
        moreArticles = moreArticles.concat(moreArticlesResponse?.articleList);
        skip = skip + ARTICLE_LISTING_SKIP;
      }
    } else if (category != 'news') {
      const res = await fetch(`/api/articles?skip=${skip}&category=${capitaliseString(category)}`);
      const moreArticleList = await res.json();
      if (moreArticleList?.length < totalArticle) {
        moreArticles = moreArticles.concat(moreArticleList);
        skip = skip + ARTICLE_LISTING_SKIP;
      }
    } else {
      const res = await fetch(`/api/allArticles?skip=${skip}`);
      const moreArticleList = await res.json();
      if (moreArticleList?.length < totalArticle) {
        moreArticles = moreArticles.concat(moreArticleList);
        skip = skip + ARTICLE_LISTING_SKIP;
      }
    }
  };

  const fetchFilteredArticles = async (e) => {
    if (e.key === 'Enter' && e.target.value.length >= 3) {
      resetMoreArticles();
      return goto(`/blogs/news?search=${e.target.value}`, {
        replaceState: false,
        noScroll: true,
        keepFocus: true,
        invalidateAll: true,
      });
    }
  };

  const clearArtileFilter = () => {
    resetMoreArticles();
    return goto('/blogs/news', {
      replaceState: false,
      noScroll: true,
      keepFocus: true,
      invalidateAll: true,
    });
  };
</script>

{#if data?.pageConfig?.headerConfig.headerContentType === 'titleDescription'}
  <ThemedHeader
    backgroundColor={data?.pageConfig?.headerConfig.backgroundColor}
    title={data?.pageConfig?.headerConfig.headerTitle}
    description={data?.pageConfig?.headerConfig.headerDescription}
  />
{:else}
  <ThemedHeader
    backgroundColor={data?.pageConfig?.headerConfig.backgroundColor}
    customClass="absolute !h-[11rem] sm:!h-[20.125rem] left-0 right-0 -z-10"
  />
  <!-- 134px is the header + promotional banner height -->
{/if}
<div
  class="flex flex-col max-w-1280 mx-auto bg-primary-5 gap-12 p-4 pt-8 mb-16 lg:p-8 lg:gap-16 lg:mb-24"
>
  {#if data?.pageConfig?.showBreadCrumbs}
    <Breadcrumbs
      links={[
        { label: 'Sweat', href: '/' },
        { label: 'Blog', href: '/blogs/news' },
        {
          label: $page.params.category,
          href: `/blogs/${$page.params.category.toLocaleLowerCase()}`,
          isCurrentUrl: true,
        },
      ]}
      currentTextColor="text-primary-500"
      textColor="text-primary-200"
      chevronColor="#FFCCDD"
    />
  {/if}
  <ArticleStandardCard
    title={heroArticle?.title}
    subHeading={heroArticle?.category}
    image={heroArticle?.heroImage}
    link={`/blogs/${heroArticle?.category}${heroArticle?.slug}`}
    customImageClass="w-full h-[240px] sm:h-[596px] lg:object-top"
    cardSize="large"
    customClass="text-center -mt-4 lg:mt-0"
    customTitleClass="!font-bold"
    headingLevel={data?.pageConfig?.headerConfig.headerContentType === 'titleDescription'
      ? 'h3'
      : 'h1'}
  />
  {#if featuredArticlesCollection?.length}
    <div
      style:grid-template-columns="repeat({featuredArticlesCollection?.length}, 1fr)"
      class="flex flex-col gap-12 sm:grid sm:gap-8"
    >
      {#each featuredArticlesCollection as { slug, title, category, heroImage }}
        <ArticleStandardCard
          {title}
          subHeading={category}
          image={heroImage}
          link={`/blogs/${category}${slug}`}
          customClass="text-center sm:text-left"
          customImageClass="w-full h-[240px] sm:h-[370px]"
          customTitleClass="!font-bold"
        />
      {/each}
    </div>
  {/if}
  {#if ARTICLE_CATEGORY_CARDS.length && data.pageConfig.showCategorySelector}
    <div class="lg:hidden w-[calc(100%+1rem)]">
      <Carousel
        bind:this={carousel}
        arrows={false}
        dots={false}
        particlesToShow={1.5}
        infinite={false}
      >
        {#each ARTICLE_CATEGORY_CARDS as categoryCard, index}
          <a
            class="flex object-cover rounded-xxl h-[200px] sm:h-[340px] w-[220px]"
            style="margin-right: {index !== ARTICLE_CATEGORY_CARDS.length - 1 ? '10px' : '0'}"
            href={categoryCard.url}
          >
            <div
              class="bg-center bg-no-repeat bg-cover border rounded-xl h-full w-full relative flex flex-col-reverse"
              style="background-image: linear-gradient(178deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%), url({categoryCard?.imageSrc});"
            >
              <h3
                class="relative bottom-4 left-5 text-white text-display-xxs md:text-30 font-semibold md:font-bold"
              >
                {categoryCard.url.replace('/blogs/', '').charAt(0).toUpperCase()}{categoryCard.url
                  .replace('/blogs/', '')
                  .substring(1)}
              </h3>
            </div>
          </a>
        {/each}
      </Carousel>
    </div>
    <section class="hidden lg:grid lg:grid-cols-4 lg:grid-flow-row lg:gap-8">
      {#each ARTICLE_CATEGORY_CARDS as categoryCard}
        <a href={categoryCard.url} class="h-[212px]">
          <div
            class="bg-center bg-no-repeat bg-cover border rounded-xl h-full w-full relative flex flex-col-reverse"
            style="background-image: linear-gradient(178deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 50%, rgba(0,0,0,0.7) 100%), url({categoryCard?.imageSrc});"
          >
            <h3 class="relative bottom-4 left-5 text-white text-30 font-bold">
              {categoryCard.url.replace('/blogs/', '').charAt(0).toUpperCase()}{categoryCard.url
                .replace('/blogs/', '')
                .substring(1)}
            </h3>
          </div>
        </a>
      {/each}
    </section>
  {/if}
  <div class="relative grid grid-cols-1 gap-4 sm:grid-flow-col sm:grid-cols-[1fr_383px]">
    <div class="grid grid-flow-row gap-3">
      <p class="text-primary-500 text-sm sm:text-md font-semibold">Explore the Sweat Blog</p>
      <h2 class="text-display-xxs text-gray-900 font-semibold text-left lg:text-display-xl">
        {data.pageConfig.browseArticleText}
      </h2>
    </div>
    <!--<TextInput
      customClass="mt-4 sm:mt-0 sm:self-end sm:mb-2"
      customInputClass="pl-10 pr-10"
      placeholder="Search"
      on:keydown={fetchFilteredArticles}
      handleKeydown={fetchFilteredArticles}
      value={searchParams || undefined}
    >
      <SearchIcon slot="beforeInput" customClass="absolute top-[50%] translate-y-[-50%] left-3" />
      <button
        slot="afterInput"
        class="absolute top-[50%] translate-y-[-50%] right-3 {clearButtonDisplay}"
        role="button"
        aria-label="close artile filter"
        on:click={clearArtileFilter}
      >
        <CloseIcon />
      </button>
    </TextInput>
    {#if totalArticle && searchParams}
      <span class="absolute -bottom-8 right-0 text-gray-600">{totalArticle} results</span>
    {/if}-->
  </div>
  {#if data?.pageData?.articles.articleList?.length}
    <div class="grid grid-cols-1 gap-12 sm:grid-cols-3 sm:items-baseline sm:gap-x-8 sm:gap-y-16">
      {#each data?.pageData?.articles.articleList as { slug, title, category, heroImage }}
        <ArticleStandardCard
          {title}
          subHeading={category}
          image={heroImage}
          link={`/blogs/${category}${slug}`}
        />
      {/each}
      {#each moreArticles as { slug, title, category, heroImage }}
        <ArticleStandardCard
          {title}
          subHeading={category}
          image={heroImage}
          link={`/blogs/${category}${slug}`}
        />
      {/each}
    </div>
  {:else}
    <p class="text-xxl text-center">
      We couldn't find anything, go back to <a class="underline" href="/blogs/news"
        >blog main page</a
      >
    </p>
  {/if}
  {#if displayLoadMoreButton}
    <Button
      text="Load More"
      variant={BUTTON_VARIANT.OUTLINED}
      buttonSize={BUTTON_SIZE.LARGE}
      customClass="py-3 self-center"
      onClick={fetchMoreArticles}
    />
  {/if}
</div>
